'use client';

import { useEffect, useRef, useState } from 'react';
import type { TransitionEvent } from 'react';
import { useGetShareSignatureQuery } from '@/app/(main)/client-detail/hooks/useGetShareSignatureQuery';
import { BaseButton } from '@/app/components/buttons/BaseButton';
import { TextArea } from '@/app/components/form/TextArea';
import { textAreaErrorMessageStyles } from '@/app/components/form/TextArea/styles';
import { COLLAPSIBLE_WRAPPER_CLASSES, collapsibleStateClasses } from '@/utils/collapsibleClasses';
import { cn } from '@/utils/сn';
import { canConfirmSignatureDraft, resolveSignatureAfterEdit } from '../../../../utils/resolveSignatureAfterEdit';
import { MAX_SIGNATURE_LENGTH } from '../../emailFormSchema';
import { SIGNATURE_EDIT_HELPER_COPY, SIGNATURE_SUFFIX_COPY } from './consts';
import {
  SIGNATURE_PANE_BODY_CLASSES,
  SIGNATURE_PANE_CLIP_CLASSES,
  SIGNATURE_READ_ONLY_BOX_CLASSES,
  signatureSkeletonBarStyles,
} from './styles';

type SignatureSectionProps = {
  clientId: number;
  signature: string | null;
  isEditing: boolean;
  disabled?: boolean;
  errorMessage?: string;
  onEditingChange: (isEditing: boolean) => void;
  onSignatureChange: (signature: string | null) => void;
};

type SignatureEditSession = {
  initialSignature: string;
  draft: string;
};

const SIGNATURE_SKELETON_LINES = ['name', 'agency', 'email'] as const;

export const SignatureSection = ({
  clientId,
  signature,
  isEditing,
  disabled = false,
  errorMessage,
  onEditingChange,
  onSignatureChange,
}: SignatureSectionProps) => {
  const { data: defaultSignature, isPending, error } = useGetShareSignatureQuery(clientId);
  const [editSession, setEditSession] = useState<SignatureEditSession | null>(null);
  const draftRef = useRef<HTMLTextAreaElement>(null);
  const editorPaneRef = useRef<HTMLDivElement>(null);
  const editHeaderRef = useRef<HTMLDivElement>(null);
  const shouldRefocusEditButtonRef = useRef(false);
  const isEditorOpen = isEditing && editSession !== null;

  useEffect(() => {
    if (error) {
      console.warn('Send Forms: the default signature did not load; the server adds it on send', error);
    }
  }, [error]);

  useEffect(() => {
    if (!isEditorOpen) {
      if (shouldRefocusEditButtonRef.current) {
        shouldRefocusEditButtonRef.current = false;
        editHeaderRef.current?.querySelector<HTMLButtonElement>('button')?.focus({ preventScroll: true });
      }
      return;
    }
    draftRef.current?.focus({ preventScroll: true });
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      editorPaneRef.current?.scrollIntoView({ block: 'nearest' });
    }
  }, [isEditorOpen]);

  const handleEditorPaneTransitionEnd = (event: TransitionEvent<HTMLDivElement>) => {
    if (isEditorOpen && event.target === event.currentTarget && event.propertyName === 'grid-template-rows') {
      event.currentTarget.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  };

  const shownSignature = signature ?? defaultSignature ?? '';
  const isDefaultLoading = signature === null && isPending;
  const isEditDisabled = disabled || isDefaultLoading;

  const handleEditStart = () => {
    setEditSession({ initialSignature: shownSignature, draft: shownSignature });
    onEditingChange(true);
  };

  const closeEditor = () => {
    shouldRefocusEditButtonRef.current = true;
    onEditingChange(false);
  };

  const handleEditCancel = closeEditor;

  const handleEditDone = () => {
    if (!editSession) return;

    const nextSignature = resolveSignatureAfterEdit({ ...editSession, currentSignature: signature, defaultSignature });
    if (nextSignature !== signature) onSignatureChange(nextSignature);
    closeEditor();
  };

  return (
    <div className="border-t border-grey-200">
      <div
        ref={editorPaneRef}
        inert={!isEditorOpen}
        className={cn(COLLAPSIBLE_WRAPPER_CLASSES, collapsibleStateClasses(!isEditorOpen))}
        onTransitionEnd={handleEditorPaneTransitionEnd}
      >
        <div className={SIGNATURE_PANE_CLIP_CLASSES}>
          <div className={SIGNATURE_PANE_BODY_CLASSES}>
            <TextArea
              ref={draftRef}
              id="signatureDraft"
              name="signatureDraft"
              label="Signature"
              labelStyle={{ intent: 'small' }}
              maxLength={MAX_SIGNATURE_LENGTH}
              disableResize
              value={editSession?.draft ?? ''}
              onChange={(event) => {
                const draft = event.target.value;
                setEditSession((session) => session && { ...session, draft });
              }}
            />
            <p className="text-body-small text-blue-700">{SIGNATURE_EDIT_HELPER_COPY}</p>
            <div className="flex justify-end gap-3">
              <BaseButton intent="secondary" size="medium" type="button" onClick={handleEditCancel}>
                Cancel
              </BaseButton>
              <BaseButton
                intent="primary"
                size="medium"
                type="button"
                disabled={!editSession || !canConfirmSignatureDraft(editSession.draft)}
                onClick={handleEditDone}
              >
                Done
              </BaseButton>
            </div>
          </div>
        </div>
      </div>
      <div inert={isEditorOpen} className={cn(COLLAPSIBLE_WRAPPER_CLASSES, collapsibleStateClasses(isEditorOpen))}>
        <div className={SIGNATURE_PANE_CLIP_CLASSES}>
          <div className={SIGNATURE_PANE_BODY_CLASSES}>
            <div ref={editHeaderRef} className="flex items-center justify-between gap-2">
              <p className="text-body-small font-medium text-black">
                Signature{' '}
                <span className="text-button-regular text-grey-700">
                  {signature === null ? SIGNATURE_SUFFIX_COPY.autoAdded : SIGNATURE_SUFFIX_COPY.customized}
                </span>
              </p>
              <BaseButton intent="inline" type="button" disabled={isEditDisabled} onClick={handleEditStart}>
                Edit Signature
              </BaseButton>
            </div>
            <div aria-busy={isDefaultLoading} aria-live="polite" className={SIGNATURE_READ_ONLY_BOX_CLASSES}>
              {isDefaultLoading ? (
                <div aria-hidden className="flex animate-pulse flex-col">
                  {SIGNATURE_SKELETON_LINES.map((line) => (
                    <div key={line} className="flex h-5 items-center">
                      <div className={signatureSkeletonBarStyles({ line })} />
                    </div>
                  ))}
                </div>
              ) : (
                shownSignature
              )}
            </div>
            {errorMessage && <p className={textAreaErrorMessageStyles({ position: 'relative' })}>{errorMessage}</p>}
          </div>
        </div>
      </div>
    </div>
  );
};
